import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';
import {
  CHARACTER_META,
  REACTION_IDS,
  REACTION_META,
  ROW_SEAT_COUNTS,
  THROWABLES,
  seatPercent,
  type CharacterId,
  type ReactionId,
  type SeatId,
  type ThrowableId,
} from '../../theater/catalog';
import type { TheaterPreferences } from '../../lib/api';
import { blockedLanguageError } from '../../lib/contentPolicy';
import TheaterCharacter, { type CharacterAnimState } from './TheaterCharacter';
import TheaterEffects, { type ActiveThrow } from './TheaterEffects';
import TheaterSeat from './TheaterSeat';
import './VirtualTheater.css';

export interface TheaterOccupantView {
  userId: string;
  username: string;
  displayName: string | null;
  character: CharacterId;
  seat: SeatId;
  seatKey: string;
  joinedAt: number;
}

export interface TheaterChatMsg {
  id: string;
  userId: string;
  username: string;
  character: CharacterId;
  seatKey: string;
  message: string;
  timestamp: number;
}

export interface TheaterReactionView {
  id: string;
  userId: string;
  seatKey: string;
  reaction: ReactionId;
  durationMs: number;
  timestamp: number;
}

export interface RareAnnouncement {
  id: string;
  announcement: string;
  durationMs: number;
  timestamp: number;
}

interface LocalOccupant extends TheaterOccupantView {
  anim: CharacterAnimState;
  posX: number;
  posY: number;
  entering?: boolean;
  leaving?: boolean;
}

const THROW_COOLDOWN_MS = 2000;

function randomScreenTarget(): { x: number; y: number } {
  return {
    x: 0.2 + Math.random() * 0.6,
    y: 0.2 + Math.random() * 0.55,
  };
}

function nearby(a: string, b: string) {
  const [ar, ac] = a.split(':').map(Number);
  const [br, bc] = b.split(':').map(Number);
  return Math.abs(ar - br) <= 1 && Math.abs(ac - bc) <= 1;
}

export default function VirtualTheater({
  screenSlot,
  belowScreen,
  userId: _userId,
  preferences,
  occupants,
  inTheater,
  joinError,
  chatMessages,
  reactions,
  throws,
  rareAnnouncement,
  throwables,
  onJoin,
  onLeave,
  onChat,
  onThrow,
  onReaction,
  notice,
  watchOnly = false,
  onSignIn,
}: {
  screenSlot: ReactNode;
  belowScreen?: ReactNode;
  userId: string;
  preferences: TheaterPreferences;
  occupants: TheaterOccupantView[];
  inTheater: boolean;
  joinError: string | null;
  chatMessages: TheaterChatMsg[];
  reactions: TheaterReactionView[];
  throws: ActiveThrow[];
  rareAnnouncement: RareAnnouncement | null;
  throwables: Array<{ id: ThrowableId; name: string; emoji: string; cooldownMs?: number }>;
  onJoin: () => void;
  onLeave: () => void;
  onChat: (message: string) => void;
  onThrow: (objectType: ThrowableId, targetX: number, targetY: number) => void;
  onReaction: (reaction: ReactionId) => void;
  notice: string | null;
  watchOnly?: boolean;
  onSignIn?: () => void;
}) {
  const theaterRef = useRef<HTMLDivElement>(null);
  const screenRef = useRef<HTMLDivElement>(null);
  const [locals, setLocals] = useState<Record<string, LocalOccupant>>({});
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [chatText, setChatText] = useState('');
  const [chatError, setChatError] = useState<string | null>(null);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [throwCooldownUntil, setThrowCooldownUntil] = useState(0);
  const [cooldownLeftMs, setCooldownLeftMs] = useState(0);
  const throwLockRef = useRef(false);
  const [bubbles, setBubbles] = useState<
    Array<TheaterChatMsg & { offsetX: number; offsetY: number }>
  >([]);
  const [shaking, setShaking] = useState(false);
  const known = useRef(new Set<string>());

  const occupiedKeys = useMemo(
    () => new Set(occupants.map((o) => o.seatKey)),
    [occupants],
  );

  // Sync occupants with enter/leave walk animations
  useEffect(() => {
    setLocals((prev) => {
      const next: Record<string, LocalOccupant> = { ...prev };
      const live = new Set(occupants.map((o) => o.userId));

      for (const occ of occupants) {
        const seatPos = seatPercent(occ.seat);
        const existing = next[occ.userId];
        if (!existing) {
          const fromLeft = occ.seat.col < (ROW_SEAT_COUNTS[occ.seat.row] ?? 7) / 2;
          next[occ.userId] = {
            ...occ,
            anim: 'enter',
            posX: fromLeft ? -8 : 108,
            posY: seatPos.y,
            entering: true,
          };
          window.setTimeout(() => {
            setLocals((cur) => {
              const me = cur[occ.userId];
              if (!me) return cur;
              return {
                ...cur,
                [occ.userId]: {
                  ...me,
                  anim: 'walk',
                  posX: seatPos.x,
                  posY: seatPos.y,
                },
              };
            });
            window.setTimeout(() => {
              setLocals((cur) => {
                const me = cur[occ.userId];
                if (!me) return cur;
                return {
                  ...cur,
                  [occ.userId]: { ...me, anim: 'sit', entering: false },
                };
              });
            }, 2800);
          }, 40);
          known.current.add(occ.userId);
        } else {
          next[occ.userId] = {
            ...existing,
            ...occ,
            anim: existing.entering || existing.leaving ? existing.anim : existing.anim,
            posX: existing.leaving || existing.entering ? existing.posX : seatPos.x,
            posY: existing.leaving || existing.entering ? existing.posY : seatPos.y,
          };
        }
      }

      for (const id of Object.keys(next)) {
        if (!live.has(id) && !next[id]!.leaving) {
          const me = next[id]!;
          const seatsInRow = ROW_SEAT_COUNTS[me.seat.row] ?? 7;
          const toLeft = me.seat.col < seatsInRow / 2;
          next[id] = {
            ...me,
            leaving: true,
            anim: 'leave',
            posX: toLeft ? -10 : 110,
          };
          window.setTimeout(() => {
            setLocals((cur) => {
              const copy = { ...cur };
              delete copy[id];
              return copy;
            });
            known.current.delete(id);
          }, 2600);
        }
      }
      return next;
    });
  }, [occupants]);

  useEffect(() => {
    if (!preferences.speech_bubbles) {
      setBubbles([]);
      return;
    }
    const latest = chatMessages[chatMessages.length - 1];
    if (!latest) return;
    if (bubbles.some((b) => b.id === latest.id)) return;

    const neighbors = bubbles.filter(
      (b) => b.seatKey === latest.seatKey || nearby(b.seatKey, latest.seatKey),
    );
    const offsetY = -neighbors.length * 22;
    const offsetX = neighbors.length % 2 === 0 ? 0 : 14;
    const hold = Math.min(8000, Math.max(4000, 3000 + latest.message.length * 40));

    setBubbles((prev) => [...prev, { ...latest, offsetX, offsetY }]);
    window.setTimeout(() => {
      setBubbles((prev) => prev.filter((b) => b.id !== latest.id));
    }, hold);
  }, [chatMessages, preferences.speech_bubbles]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!preferences.reactions) return;
    const latest = reactions[reactions.length - 1];
    if (!latest) return;
    setLocals((cur) => {
      const me = cur[latest.userId];
      if (!me || me.entering || me.leaving) return cur;
      return { ...cur, [latest.userId]: { ...me, anim: latest.reaction } };
    });
    window.setTimeout(() => {
      setLocals((cur) => {
        const me = cur[latest.userId];
        if (!me || me.entering || me.leaving) return cur;
        return { ...cur, [latest.userId]: { ...me, anim: 'sit' } };
      });
    }, latest.durationMs || 2000);
  }, [reactions, preferences.reactions]);

  useEffect(() => {
    const latest = throws[throws.length - 1];
    if (!latest) return;
    const thrower = Object.values(locals).find((o) => o.username === latest.username);
    if (!thrower) return;
    setLocals((cur) => {
      const me = cur[thrower.userId];
      if (!me) return cur;
      return { ...cur, [thrower.userId]: { ...me, anim: 'throw' } };
    });
    window.setTimeout(() => {
      setLocals((cur) => {
        const me = cur[thrower.userId];
        if (!me || me.entering || me.leaving) return cur;
        return { ...cur, [thrower.userId]: { ...me, anim: 'sit' } };
      });
    }, 500);

    if (latest.rareEvent === 'nuclear-tomato') {
      setShaking(true);
      window.setTimeout(() => setShaking(false), 700);
    }
  }, [throws]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (throwCooldownUntil <= Date.now()) {
      setCooldownLeftMs(0);
      throwLockRef.current = false;
      return;
    }
    const tick = () => {
      const left = Math.max(0, throwCooldownUntil - Date.now());
      setCooldownLeftMs(left);
      if (left <= 0) {
        setThrowCooldownUntil(0);
        throwLockRef.current = false;
      }
    };
    tick();
    const id = window.setInterval(tick, 250);
    return () => window.clearInterval(id);
  }, [throwCooldownUntil]);

  const throwOnCooldown = cooldownLeftMs > 0 || throwLockRef.current;

  function handleThrowClick(objectType: ThrowableId, cooldownMs?: number) {
    if (!inTheater || !preferences.thrown_effects) return;
    if (throwLockRef.current || Date.now() < throwCooldownUntil) return;
    throwLockRef.current = true;
    const cd = Math.max(THROW_COOLDOWN_MS, cooldownMs ?? THROW_COOLDOWN_MS);
    setThrowCooldownUntil(Date.now() + cd);
    setCooldownLeftMs(cd);
    const target = randomScreenTarget();
    onThrow(objectType, target.x, target.y);
  }

  function submitChat(e: FormEvent) {
    e.preventDefault();
    const msg = chatText.trim();
    if (!msg) return;
    const languageError = blockedLanguageError(msg);
    if (languageError) {
      setChatError(languageError);
      return;
    }
    onChat(msg.slice(0, 200));
    setChatText('');
    setChatError(null);
  }

  const showCharacters = preferences.theater_characters;

  return (
    <section className={`tt-theater${shaking ? ' is-shaking' : ''}`} ref={theaterRef}>
      <div className="tt-stage">
        <div className="tt-valance" aria-hidden="true" />
        <div className="tt-curtain tt-curtain-left" aria-hidden="true" />
        <div className="tt-screen" ref={screenRef}>
          {screenSlot}
        </div>
        <div className="tt-curtain tt-curtain-right" aria-hidden="true" />
      </div>

      <div className="tt-house">
        <div className="tt-below-bar">
          {belowScreen}
        </div>
        <div className="tt-aisle-left" />
        <div className="tt-aisle-right" />
        <div className="tt-floor" />

        {notice && <div className="tt-join-notice">{notice}</div>}
        {rareAnnouncement && (
          <div className="tt-rare-banner" key={rareAnnouncement.id}>
            {rareAnnouncement.announcement}
          </div>
        )}

        <div className="tt-seating" aria-label="Theater seating">
          {/*
            MST3K layering per row (front row highest):
            1) characters (shoulders behind seat)
            2) seat humps (cover torso; heads rise above)
          */}
          {ROW_SEAT_COUNTS.map((seatsInRow, row) => {
            const rowChars = showCharacters
              ? Object.values(locals).filter((o) => o.seat.row === row)
              : [];
            return (
              <div key={`row-${row}`} className="tt-seat-row" data-row={row}>
                {rowChars.map((occ) => {
                  const bubble = bubbles.filter((b) => b.userId === occ.userId);
                  const bubbleLift = [56, 72, 92][occ.seat.row] ?? 72;
                  const walking =
                    occ.anim === 'walk' ||
                    occ.anim === 'enter' ||
                    occ.anim === 'leave';
                  return (
                    <div key={occ.userId}>
                      {preferences.speech_bubbles &&
                        bubble.map((b) => (
                          <div
                            key={b.id}
                            className="tt-speech"
                            style={{
                              left: `${occ.posX}%`,
                              top: `${occ.posY}%`,
                              transform: `translate(calc(-50% + ${b.offsetX}px), calc(-100% + ${b.offsetY - bubbleLift}px))`,
                              zIndex: 40 + occ.seat.row,
                            }}
                          >
                            <div className="tt-speech-user">{b.username}</div>
                            <div className="tt-speech-text">{b.message}</div>
                          </div>
                        ))}
                      <TheaterCharacter
                        character={occ.character}
                        username={occ.displayName || occ.username}
                        state={occ.anim}
                        row={occ.seat.row}
                        selected={selectedUser === occ.userId}
                        onSelect={() =>
                          setSelectedUser((u) =>
                            u === occ.userId ? null : occ.userId,
                          )
                        }
                        style={{
                          left: `${occ.posX}%`,
                          top: `${occ.posY}%`,
                          transition: 'left 2.6s linear, top 0.85s ease',
                          zIndex: walking ? 50 + row : 10 + row * 2,
                        }}
                      />
                    </div>
                  );
                })}

                {Array.from({ length: seatsInRow }, (_, col) => {
                  const key = `${row}:${col}`;
                  const pos = seatPercent({ row, col });
                  return (
                    <div
                      key={key}
                      style={{
                        position: 'absolute',
                        left: `${pos.x}%`,
                        top: `${pos.y}%`,
                        zIndex: 11 + row * 2,
                      }}
                    >
                      <TheaterSeat
                        row={row}
                        col={col}
                        occupied={occupiedKeys.has(key)}
                      />
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>

      <TheaterEffects
        throws={throws}
        enabled={preferences.thrown_effects}
        screenRef={screenRef}
        theaterRef={theaterRef}
      />

      <div className="tt-toolbar panel">
        {watchOnly ? (
          <div className="tt-join-row">
            <p className="tt-watch-copy">
              You can watch from the aisle. Sign in to sit down, throw stuff, chat, and request videos.
            </p>
            <button className="btn btn-gold" type="button" onClick={() => onSignIn?.()}>
              Sign in to sit down
            </button>
          </div>
        ) : !inTheater ? (
          <div className="tt-join-row">
            <button className="btn btn-gold" type="button" onClick={onJoin}>
              Watch Movie
            </button>
            {joinError && <span className="error-banner">{joinError}</span>}
          </div>
        ) : (
          <>
            <div className="tt-emote-block">
              <div className="tt-emote-row">
                <span className="tt-toolbar-label">Throw</span>
                {(throwables.length
                  ? throwables
                  : Object.values(THROWABLES).map((t) => ({
                      id: t.id,
                      name: t.name,
                      emoji: t.emoji,
                      cooldownMs: THROW_COOLDOWN_MS,
                    }))
                ).map((t) => (
                  <button
                    key={t.id}
                    type="button"
                    className={`tt-emote-btn${throwOnCooldown ? ' is-cooling' : ''}`}
                    title={
                      throwOnCooldown
                        ? `Cooldown ${(cooldownLeftMs / 1000).toFixed(1)}s`
                        : `Throw ${t.name}`
                    }
                    disabled={!preferences.thrown_effects || throwOnCooldown}
                    onClick={() => handleThrowClick(t.id, t.cooldownMs)}
                  >
                    {t.emoji}
                  </button>
                ))}
                {throwOnCooldown && (
                  <span className="tt-cooldown-label">
                    {(cooldownLeftMs / 1000).toFixed(1)}s
                  </span>
                )}
              </div>
            </div>

            <div className="tt-emote-block">
              <div className="tt-emote-row">
                <span className="tt-toolbar-label">React</span>
                {REACTION_IDS.map((id) => (
                  <button
                    key={id}
                    type="button"
                    className="tt-emote-btn"
                    title={REACTION_META[id].name}
                    disabled={!preferences.reactions}
                    onClick={() => onReaction(id)}
                  >
                    {REACTION_META[id].emoji}
                  </button>
                ))}
                <button className="btn btn-ghost" type="button" onClick={onLeave}>
                  Leave seat
                </button>
              </div>
            </div>

            <form className="tt-chat-form" onSubmit={submitChat}>
              <input
                className="field"
                placeholder="Say something (keep it PG)..."
                value={chatText}
                maxLength={200}
                onChange={(e) => {
                  setChatText(e.target.value);
                  if (chatError) setChatError(null);
                }}
              />
              <button className="btn btn-primary" type="submit">
                Send
              </button>
              <button
                className="btn"
                type="button"
                onClick={() => setHistoryOpen((v) => !v)}
              >
                Chat History
              </button>
              {chatError && (
                <p className="tt-chat-error" role="alert">
                  {chatError}
                </p>
              )}
            </form>
          </>
        )}
      </div>

      {historyOpen && (
        <div className="tt-history panel">
          <div className="tt-history-head">
            <strong>Recent chat</strong>
            <button className="btn" type="button" onClick={() => setHistoryOpen(false)}>
              Close
            </button>
          </div>
          <ul className="tt-history-list">
            {chatMessages.slice(-50).map((m) => (
              <li key={m.id}>
                <span className="tt-history-user">
                  {CHARACTER_META[m.character]?.emoji} {m.username}
                </span>
                <span>{m.message}</span>
              </li>
            ))}
            {chatMessages.length === 0 && <li className="muted">No messages yet.</li>}
          </ul>
        </div>
      )}
    </section>
  );
}
